import FocusLock from 'react-focus-lock';

import { Box } from '@reckon-web/box';
import { ActionButton } from '@reckon-web/button';
import { ChevronDownIcon } from '@reckon-web/icon';
import { ChevronUpIcon } from '@reckon-web/icon';
import { PopoverDialog, usePopover } from '@reckon-web/popover';
import { useId } from '@reckon-web/utils';

import { Menu } from './styled-components';
import { MENU_ALIGNMENT } from './constants';
import { ConfigMenuProps } from './types';

export const ConfigMenu = ({
  align = 'left',
  children,
  trigger,
  ...props
}: ConfigMenuProps) => {
  // setup internal
  const menuId = useId();

  const {
    isOpen,
    setOpen,
    dialog,
    trigger: popoverTrigger,
  } = usePopover({
    placement: MENU_ALIGNMENT[align],
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  // trigger UI
  const triggerProps = {
    ref: popoverTrigger.ref,
    onClick: () => setOpen((s) => !s),
    ...popoverTrigger.props,
    'aria-controls': menuId,
  } as const;
  const triggerElement =
    typeof trigger === 'function' ? (
      trigger({ isOpen, triggerProps })
    ) : (
      <ActionButton
        iconAfter={isOpen ? ChevronUpIcon : ChevronDownIcon}
        label={trigger}
        {...triggerProps}
      />
    );

  // We need a reference to the menu element to prepare keyboard navigation, but
  // we don't want the menu items to be focusable until the menu is open
  const menuContent = isOpen ? (
    <FocusLock autoFocus returnFocus lockProps={{ role: 'presentation' }}>
      {children}
    </FocusLock>
  ) : null;

  return (
    <Box {...props}>
      {triggerElement}
      <PopoverDialog ref={dialog.ref} isVisible={isOpen} {...dialog.props}>
        <Menu id={menuId}>{menuContent}</Menu>
      </PopoverDialog>
    </Box>
  );
};
